import React, { useState } from 'react';
import { CheckCircle2, Loader2, Send, Phone } from 'lucide-react';
import { Button } from '../ui/button';
import { CONTACT_METHODS } from '../../data/enquiry';
import { PRACTICE, WHATSAPP_LINK } from '../../data/practice';
import { SUITABILITY } from '../../data/sedation';

const API = process.env.REACT_APP_BACKEND_URL;
const FIELD =
  'mt-1.5 w-full rounded-xl border border-[#006A71]/20 bg-white px-4 py-3 text-rabbit placeholder:text-chocolate-malt/45 transition-colors duration-200 focus:border-[#01A9B4] focus:outline-none focus:ring-2 focus:ring-[#01A9B4]/40 motion-reduce:transition-none';

const Label = ({ htmlFor, children }) => (
  <label htmlFor={htmlFor} className="block text-sm font-medium text-rabbit">{children}</label>
);

const Sent = ({ reference, tid }) => (
  <div className="rounded-2xl border border-[#01A9B4]/35 bg-[#01A9B4]/[0.06] p-8 md:p-10" role="status" data-testid={`${tid}-success`}>
    <CheckCircle2 className="mb-5 h-10 w-10 text-[#006A71]" strokeWidth={1.6} aria-hidden="true" />
    <h3 className="text-xl font-semibold text-rabbit">Thank you — that is the hard part done</h3>
    <p className="mt-3 text-base leading-relaxed text-chocolate-malt">
      Your reference is <strong data-testid={`${tid}-reference`}>{reference}</strong>. We will be in touch within one working day, Monday to Saturday.
    </p>
    <p className="mt-4 text-base leading-relaxed text-chocolate-malt">
      Two steps follow, and neither commits you to treatment: a phone conversation where you can ask anything, then a short in-person sedation assessment. Suitability is only ever decided at that assessment.
    </p>
    <p className="mt-5 text-sm text-chocolate-malt/80">
      Need us sooner? Call <a href={PRACTICE.phoneTel} className="font-semibold underline underline-offset-4">{PRACTICE.phoneDisplay}</a> or{' '}
      <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" className="font-semibold underline underline-offset-4">message us on WhatsApp</a>.
    </p>
  </div>
);

const summarise = (form) =>
  [
    `Treatment they think they need: ${form.treatment}`,
    `How anxious: ${form.anxiety}`,
    `Flags: ${form.flags.length ? form.flags.join(', ') : 'not answered'}`,
    `Escort: ${form.escort}`,
    form.notes.trim() ? `In their words: ${form.notes.trim()}` : '',
  ]
    .filter(Boolean)
    .join('\n');

/**
 * Sedation suitability enquiry — routes to /api/contact as the "IV Sedation"
 * category. It never gives the patient a verdict; it hands the practice the
 * answers so the follow-up call starts somewhere useful.
 */
const SedationEnquiryForm = ({ idPrefix, source, intro }) => {
  const tid = `sedation-enquiry-${idPrefix}`;
  const id = (name) => `${tid}-${name}`;
  const [form, setForm] = useState({
    treatment: '', anxiety: '', flags: [], escort: '',
    name: '', phone: '', email: '', preferred_contact: 'phone', notes: '',
  });
  const [state, setState] = useState({ busy: false, error: '', ref: '' });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const toggleFlag = (flag) => () =>
    setForm((f) => {
      const none = 'None of these';
      if (flag === none) return { ...f, flags: f.flags.includes(none) ? [] : [none] };
      const next = f.flags.filter((x) => x !== none);
      return { ...f, flags: next.includes(flag) ? next.filter((x) => x !== flag) : [...next, flag] };
    });

  const submit = async (e) => {
    e.preventDefault();
    setState({ busy: true, error: '', ref: '' });
    try {
      const res = await fetch(`${API}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: form.phone,
          preferred_contact: form.preferred_contact,
          condition: 'IV Sedation',
          message: summarise(form),
          source: source || window.location.pathname,
          sedation: {
            treatment: form.treatment,
            anxiety: form.anxiety,
            flags: form.flags,
            escort: form.escort,
            notes: form.notes.trim(),
          },
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Please check your details and try again.');
      setState({ busy: false, error: '', ref: data.referenceId });
    } catch (err) {
      setState({ busy: false, error: err.message, ref: '' });
    }
  };

  if (state.ref) return <Sent reference={state.ref} tid={tid} />;

  return (
    <form onSubmit={submit} className="rounded-2xl border border-[#006A71]/15 bg-white p-6 sm:p-9 shadow-[0_20px_60px_-45px_rgba(0,106,113,0.6)]" data-testid={tid}>
      <h2 className="text-2xl md:text-3xl font-semibold text-rabbit">Check if sedation is right for you</h2>
      {intro && <p className="mt-3 max-w-2xl text-base leading-relaxed text-chocolate-malt">{intro}</p>}

      <div className="mt-8 space-y-6">
        <div className="grid gap-6 sm:grid-cols-2">
          <div>
            <Label htmlFor={id('treatment')}>What do you think you need? *</Label>
            <select id={id('treatment')} required value={form.treatment} onChange={set('treatment')} className={FIELD} data-testid={`${tid}-treatment`}>
              <option value="" disabled>Choose the closest option…</option>
              {SUITABILITY.treatments.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <Label htmlFor={id('anxiety')}>How anxious are you about it? *</Label>
            <select id={id('anxiety')} required value={form.anxiety} onChange={set('anxiety')} className={FIELD} data-testid={`${tid}-anxiety`}>
              <option value="" disabled>Choose the closest option…</option>
              {SUITABILITY.anxiety.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
        </div>

        <fieldset>
          <legend className="text-sm font-medium text-rabbit">Does anything here apply to you?</legend>
          <p className="mt-1 text-sm text-chocolate-malt/75">Tick anything that does. Nothing here rules you out by itself — it tells us what to ask about.</p>
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            {SUITABILITY.flags.map((flag) => (
              <label key={flag} className="flex cursor-pointer items-start gap-3 rounded-xl border border-[#006A71]/15 bg-white px-4 py-3 text-sm text-chocolate-malt transition-colors duration-200 hover:border-[#01A9B4]/45 motion-reduce:transition-none">
                <input
                  type="checkbox"
                  checked={form.flags.includes(flag)}
                  onChange={toggleFlag(flag)}
                  className="mt-0.5 h-4 w-4 flex-shrink-0 accent-[#006A71]"
                  data-testid={`${tid}-flag-${flag.toLowerCase().replace(/[^a-z]+/g, '-').replace(/^-|-$/g, '')}`}
                />
                <span>{flag}</span>
              </label>
            ))}
          </div>
        </fieldset>

        <div>
          <Label htmlFor={id('escort')}>Can someone bring you and take you home? *</Label>
          <select id={id('escort')} required value={form.escort} onChange={set('escort')} className={FIELD} data-testid={`${tid}-escort`}>
            <option value="" disabled>Choose the closest option…</option>
            {SUITABILITY.escort.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
          <p className="mt-1.5 text-sm text-chocolate-malt/75">An adult escort is required for IV sedation. If it is difficult, say so — we will work around the timing.</p>
        </div>

        <div className="grid gap-6 sm:grid-cols-2">
          <div>
            <Label htmlFor={id('name')}>Your name *</Label>
            <input id={id('name')} required minLength={2} autoComplete="name" value={form.name} onChange={set('name')} placeholder="Jane Smith" className={FIELD} data-testid={`${tid}-name`} />
          </div>
          <div>
            <Label htmlFor={id('phone')}>Phone number *</Label>
            <input id={id('phone')} type="tel" required minLength={7} autoComplete="tel" value={form.phone} onChange={set('phone')} placeholder="07700 900123" className={FIELD} data-testid={`${tid}-phone`} />
          </div>
          <div>
            <Label htmlFor={id('email')}>Email *</Label>
            <input id={id('email')} type="email" required autoComplete="email" value={form.email} onChange={set('email')} placeholder="jane@example.com" className={FIELD} data-testid={`${tid}-email`} />
          </div>
          <div>
            <Label htmlFor={id('method')}>How should we contact you? *</Label>
            <select id={id('method')} required value={form.preferred_contact} onChange={set('preferred_contact')} className={FIELD} data-testid={`${tid}-method`}>
              {CONTACT_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </div>
        </div>

        <div>
          <Label htmlFor={id('notes')}>Anything you would like us to know? (optional)</Label>
          <textarea id={id('notes')} rows={4} maxLength={2000} value={form.notes} onChange={set('notes')} placeholder="What you are dreading, what has happened before, or times that suit you." className={FIELD} data-testid={`${tid}-notes`} />
        </div>
      </div>

      {state.error && <p className="mt-5 text-sm text-red-600" role="alert" data-testid={`${tid}-error`}>{state.error}</p>}

      <div className="mt-8 flex flex-col gap-5 sm:flex-row sm:items-center">
        <Button type="submit" size="lg" disabled={state.busy} className="bg-[#006A71] text-white hover:bg-[#01A9B4]" data-testid={`${tid}-submit`}>
          {state.busy
            ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" aria-hidden="true" />Sending…</>
            : <><Send className="mr-2 h-5 w-5" strokeWidth={1.8} aria-hidden="true" />Send my enquiry</>}
        </Button>
        <p className="text-sm text-chocolate-malt/75">
          Or just call <a href={PRACTICE.phoneTel} className="font-semibold text-[#006A71] underline underline-offset-4" data-testid={`${tid}-call`}><Phone className="mr-1 inline h-4 w-4" aria-hidden="true" />{PRACTICE.phoneDisplay}</a>. We reply within one working day, Monday to Saturday.
        </p>
      </div>
      <p className="mt-5 text-sm leading-relaxed text-chocolate-malt/70">
        This form does not decide whether sedation is suitable for you, and it is not a medical assessment. Your answers go to the practice so the conversation can start properly. Suitability is decided in person.
      </p>
    </form>
  );
};

export default SedationEnquiryForm;
